import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Typography, IconButton, Chip, Button, Dialog, DialogTitle,
    DialogContent, DialogActions, Avatar, Skeleton, Switch, Tooltip,
    TextField, MenuItem, Badge, Pagination, CircularProgress, Tabs, Tab
} from '@mui/material';
import {
    MdAdd, MdCheckCircle, MdClose, MdDeleteForever, MdEdit, MdEmail, MdGroups, MdPauseCircle,
    MdPhone, MdSearch, MdSecurity, MdVisibility, MdWarehouse
} from 'react-icons/md';
import UserModal from '../../components/Users/UserModal';
import { getData, postData, postFileUpload, deleteData, putData } from '../../Webservice/Webservice';
import USER_ROUTES from '../../API_Route/UserRoute';

const FONT = 'Mulish, sans-serif';

const roleColorMap = {
    super_admin: { bg: '#dcfce7', color: '#166534' },
    admin: { bg: '#dbeafe', color: '#1e40af' },
    hr: { bg: '#e0f2fe', color: '#075985' },
    product_manager: { bg: '#ede9fe', color: '#5b21b6' },
    order_manager: { bg: '#ffedd5', color: '#9a3412' },
    support_staff: { bg: '#d1fae5', color: '#065f46' },
    warehouse_admin: { bg: '#fce7f3', color: '#9d174d' },
    relationship_manager: { bg: '#e0f2fe', color: '#0369a1' },
    influencer: { bg: '#faf5ff', color: '#6b21a8' },
};

const getRoleColor = (roleName) => {
    const key = (roleName || '').toLowerCase().replace(/ /g, '_');
    return roleColorMap[key] || { bg: '#f1f5f9', color: '#475569' };
};

const getRoleLabel = (user) => {
    const name = user.role_id?.name || user.role || '';
    return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || 'N/A';
};

// older records may hold "inActive"; treat any casing as inactive
const isActiveUser = (u) => String(u?.status || '').toLowerCase() === 'active';
const initialsOf = (u) => `${(u?.firstName || '')[0] || ''}${(u?.lastName || '')[0] || ''}`.toUpperCase() || '?';
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const iconBtn = (color, bg) => ({ color, background: bg, borderRadius: '8px', '&:hover': { background: bg, filter: 'brightness(.95)' } });

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const relTime = (d) => {
    if (!d) return '—';
    const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)} min ago`;
    if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
    const days = Math.floor(s / 86400);
    return days < 30 ? `${days} day${days === 1 ? '' : 's'} ago` : fmtDate(d);
};
// "Chrome on Windows" from a user-agent string
const describeAgent = (ua = '') => {
    const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
    const os = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : 'unknown device';
    return `${browser} on ${os}`;
};

// R / C / U / D chips for a menu's permissions
const PermBadges = ({ p = {} }) => (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
        {[['read', 'View', '#0369a1', '#e0f2fe'], ['create', 'Add', '#166534', '#dcfce7'], ['update', 'Edit', '#92400e', '#fef3c7'], ['delete', 'Delete', '#991b1b', '#fee2e2']].map(([k, label, fg, bg]) => (
            <Chip key={k} size="small" label={label} sx={{ fontFamily: FONT, fontWeight: 700, height: 20, fontSize: 11, background: p[k] ? bg : '#f1f5f9', color: p[k] ? fg : '#cbd5e1', textDecoration: p[k] ? 'none' : 'line-through' }} />
        ))}
    </Box>
);

const PAGE_SIZE = 15;

const Usermangement = () => {
    const [open, setOpen] = useState(false);
    const [newUser, setNewUser] = useState({
        firstName: '', lastName: '', email: '', phoneNumber: '',
        role_id: '', password: '', status: 'active',
        permissions: [], warehouseId: '', image: '', createdBy: ''
    });
    const [userList, setUserList] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [allPermissionsList, setAllPermissionsList] = useState([]);
    const [roles, setRoles] = useState([]);
    const [grantTarget, setGrantTarget] = useState(null);
    const [busy, setBusy] = useState(false);

    // View dialog
    const [viewUser, setViewUser] = useState(null);
    const [viewData, setViewData] = useState(null); // full detail from the server, null while loading
    const [viewTab, setViewTab] = useState(0);

    // Filters
    const [searchText, setSearchText] = useState('');
    const [filterRole, setFilterRole] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    // Pagination
    const [page, setPage] = useState(1);

    const token = `Bearer ${sessionStorage.getItem('token') || ''}`;

    // ── Helper: is this user an admin-panel user? ──────────────────────
    // role_id is populated with { _id, name, roleFor } from the API
    const isAdminPanelUser = (user) => {
        const roleFor = user.role_id?.roleFor;
        const roleName = user.role_id?.name?.trim().toLowerCase();
        return roleFor === 'admin' || roleName === 'warehouse_admin';
    };

    // ── Fetch ──────────────────────────────────────────────────────────
    const fetchData = async () => {
        setLoading(true);
        try {
            const [users, rolesData] = await Promise.all([
                getData(USER_ROUTES.GET_USER, undefined, token),
                getData('user/role', undefined, token)
            ]);
            setRoles(Array.isArray(rolesData) ? rolesData : []);
            setUserList((users || []).filter(isAdminPanelUser));
        } catch (e) {
            console.error('Failed to fetch data:', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []); // eslint-disable-line

    // ── Filtered + paginated list ──────────────────────────────────────
    const filteredUsers = useMemo(() => {
        const q = searchText.toLowerCase();
        return userList.filter((u) => {
            const matchSearch = !q
                || `${u.firstName} ${u.lastName}`.toLowerCase().includes(q)
                || (u.email || '').toLowerCase().includes(q)
                || (u.phoneNumber || '').includes(q)
                || (u.empId || '').toLowerCase().includes(q);
            const roleName = u.role_id?.name || u.role || '';
            const matchRole = !filterRole || roleName.toLowerCase() === filterRole.toLowerCase();
            const matchStatus = !filterStatus || (filterStatus === 'active') === isActiveUser(u);
            return matchSearch && matchRole && matchStatus;
        });
    }, [userList, searchText, filterRole, filterStatus]);

    const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
    const paginatedUsers = filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    useEffect(() => { setPage(1); }, [searchText, filterRole, filterStatus]);

    const roleOptions = useMemo(() => {
        const seen = new Set();
        userList.forEach((u) => { const name = u.role_id?.name || u.role || ''; if (name) seen.add(name); });
        return Array.from(seen);
    }, [userList]);

    const stats = useMemo(() => ({
        total: userList.length,
        active: userList.filter(isActiveUser).length,
        inactive: userList.filter((u) => !isActiveUser(u)).length,
        roles: roleOptions.length,
    }), [userList, roleOptions]);

    // ── Helpers ────────────────────────────────────────────────────────
    const refreshList = async () => {
        const users = await getData(USER_ROUTES.GET_USER, undefined, token);
        setUserList((users || []).filter(isAdminPanelUser));
    };

    // ── Handlers ───────────────────────────────────────────────────────
    const handleOpen = () => {
        const userId = sessionStorage.getItem('userId');
        setNewUser({
            firstName: '', lastName: '', email: '', phoneNumber: '',
            role_id: '', password: '', status: 'active',
            permissions: [], warehouseId: '', image: '', createdBy: userId || ''
        });
        setEditingUser(null);
        setOpen(true);
    };

    const handleEditOpen = (user) => {
        setEditingUser(user);
        setNewUser({
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            phoneNumber: user.phoneNumber || '',
            role_id: user.role_id?._id || user.role_id || '',
            password: '',
            status: isActiveUser(user) ? 'active' : 'inactive',
            permissions: user.permissions || [],
            warehouseId: (user.warehouseId && typeof user.warehouseId === 'object')
                ? user.warehouseId._id : (user.warehouseId || ''),
            image: user.image || '',
            createdBy: user.createdBy || sessionStorage.getItem('userId') || ''
        });
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditingUser(null);
    };

    const handleChange = (e) => {
        let { name, value } = e.target;
        if (name === 'mobileNumber') name = 'phoneNumber';
        if (name === 'role') name = 'role_id';
        setNewUser((prev) => ({ ...prev, [name]: value }));
    };

    const getPermissionIds = (selectedPermissions, permissionsList) => {
        const ids = [];
        permissionsList.forEach((perm) => {
            if (selectedPermissions.includes(perm.name) && perm._id) ids.push(perm._id);
            if (perm.submenus && Array.isArray(perm.submenus)) {
                perm.submenus.forEach((sub) => {
                    if (selectedPermissions.includes(perm.name + ' > ' + sub.name) && sub._id) {
                        ids.push(sub._id);
                    }
                });
            }
        });
        return ids;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const warehouseAdminRoleId = roles.find((r) => r.name?.toLowerCase() === 'warehouse_admin')?._id;
        try {
            const formData = new FormData();
            formData.append('firstName', newUser.firstName);
            formData.append('lastName', newUser.lastName);
            formData.append('email', newUser.email);
            formData.append('phoneNumber', newUser.phoneNumber);
            formData.append('status', String(newUser.status || 'active').toLowerCase());
            formData.append('createdBy', newUser.createdBy);
            formData.append('role_id', newUser.role_id);
            if (newUser.password) formData.append('password', newUser.password);

            if (newUser.role_id !== warehouseAdminRoleId && newUser.permissions?.length) {
                const ids = getPermissionIds(newUser.permissions, allPermissionsList);
                formData.append('permissions', JSON.stringify(ids));
            }
            if (newUser.role_id === warehouseAdminRoleId && newUser.warehouseId) {
                formData.append('warehouseId', newUser.warehouseId);
            }
            if (newUser.image instanceof File) {
                formData.append('image', newUser.image);
            } else if (typeof newUser.image === 'string' && newUser.image.startsWith('data:image')) {
                const arr = newUser.image.split(',');
                const mime = arr[0].match(/:(.*?);/)[1];
                const bstr = atob(arr[1]);
                let n = bstr.length;
                const u8arr = new Uint8Array(n);
                while (n--) u8arr[n] = bstr.charCodeAt(n);
                formData.append('image', new Blob([u8arr], { type: mime }), 'user_image.png');
            }

            if (editingUser?._id) {
                await putData(`user/update/${editingUser._id}`, formData, token);
            } else {
                await postFileUpload(USER_ROUTES.ADD_USER, formData, token);
            }
            await refreshList();
            handleClose();
        } catch (err) {
            console.error('Failed to save user:', err);
        }
    };

    // ── Give a warehouse admin every warehouse menu ───────────────────
    const confirmGrant = async () => {
        if (!grantTarget) return;
        setBusy(true);
        try {
            // postData shows the success / error toast itself
            await postData(`user/grant-warehouse-menus/${grantTarget._id}`, {}, token);
        } catch (e) {
            /* shown by the service */
        } finally {
            setBusy(false);
            setGrantTarget(null);
        }
    };

    // ── Quick Status Toggle ────────────────────────────────────────────
    const handleStatusToggle = async (user) => {
        const newStatus = isActiveUser(user) ? 'inactive' : 'active';
        try {
            const formData = new FormData();
            formData.append('status', newStatus);
            await putData(`user/update/${user._id}`, formData, token);
            if (viewUser?._id === user._id) setViewUser((prev) => ({ ...prev, status: newStatus }));
            await refreshList();
        } catch (e) {
            console.error('Status toggle failed:', e);
        }
    };

    // ── View ───────────────────────────────────────────────────────────
    const loadDetail = async (id) => {
        try {
            const res = await getData(`user/${id}`, undefined, token);
            setViewData(res && res.user ? res : { user: {}, accessibleMenus: [], activity: { recent: [], totalActions: 0, lastLogin: null }, sessions: null });
        } catch (e) {
            setViewData({ user: {}, accessibleMenus: [], activity: { recent: [], totalActions: 0, lastLogin: null }, sessions: null });
        }
    };

    const openView = (user) => {
        setViewUser(user);
        setViewData(null);
        setViewTab(0);
        loadDetail(user._id);
    };

    const signOutEverywhere = async () => {
        if (!viewUser) return;
        setBusy(true);
        try {
            await postData(`user/revoke-sessions/${viewUser._id}`, {}, token);
            await loadDetail(viewUser._id);
        } catch (e) {
            /* shown by the service */
        } finally {
            setBusy(false);
        }
    };

    // ── Delete ─────────────────────────────────────────────────────────
    const handleDeleteClick = (user) => { setUserToDelete(user); setDeleteDialogOpen(true); };
    const handleCloseDeleteDialog = () => { setDeleteDialogOpen(false); setUserToDelete(null); };

    const handleConfirmDelete = async () => {
        if (!userToDelete) return;
        setBusy(true);
        try {
            await deleteData(`user/delete/${userToDelete._id || userToDelete.id}`, token);
            await refreshList();
        } catch (e) {
            console.error('Delete failed:', e);
        }
        setBusy(false);
        setDeleteDialogOpen(false);
        setUserToDelete(null);
    };

    const clearFilters = () => { setSearchText(''); setFilterRole(''); setFilterStatus(''); };
    const hasActiveFilters = searchText || filterRole || filterStatus;

    // ─────────────────────────────────────────────────────────────────────
    if (open) {
        return (
            <UserModal
                open={true}
                handleClose={handleClose}
                newUser={newUser}
                handleChange={handleChange}
                handleSubmit={handleSubmit}
                isEdit={!!editingUser}
                editingUser={editingUser}
                onPermissionsLoaded={setAllPermissionsList}
                asPage={true}
            />
        );
    }

    const kpis = [
        { label: 'Team members', value: stats.total, icon: MdGroups, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', on: () => setFilterStatus(''), active: !filterStatus },
        { label: 'Active', value: stats.active, icon: MdCheckCircle, gradient: 'linear-gradient(135deg,#10b981,#059669)', on: () => setFilterStatus('active'), active: filterStatus === 'active' },
        { label: 'Inactive', value: stats.inactive, icon: MdPauseCircle, gradient: 'linear-gradient(135deg,#64748b,#475569)', on: () => setFilterStatus('inactive'), active: filterStatus === 'inactive' },
        { label: 'Roles in use', value: stats.roles, icon: MdSecurity, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', on: () => {}, active: false },
    ];


    return (
        <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', p: { xs: 1.5, md: 3 } }}>
            <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
                {/* ── Header ───────────────────────────────────────────── */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                    <Box>
                        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Users</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>Everyone who can sign in to this admin panel, and what they can open.</Typography>
                    </Box>
                    <Button variant="contained" startIcon={<MdAdd />} onClick={handleOpen}
                        sx={{ fontFamily: FONT, background: 'rgb(255 209 2)', color: 'black', textTransform: 'none', borderRadius: '10px', fontWeight: 700, px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', border: '1px solid black', '&:hover': { background: 'rgb(255 209 2)', transform: 'translateY(-1px)' } }}>
                        Create User
                    </Button>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2, mb: 3 }}>
                    {kpis.map((k) => (
                        <Paper key={k.label} elevation={0} onClick={k.on}
                            sx={{ p: 2.25, borderRadius: '16px', color: '#fff', background: k.gradient, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', outline: k.active && k.label !== 'Team members' ? '3px solid #0f172a' : 'none', outlineOffset: 2, boxShadow: '0 4px 12px rgba(15,23,42,.1)', transition: 'transform .15s', '&:hover': { transform: 'translateY(-2px)' } }}>
                            <Box sx={{ background: 'rgba(255,255,255,.22)', borderRadius: '14px', p: 1.4, display: 'flex' }}><k.icon size={24} /></Box>
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, opacity: 0.92, fontFamily: FONT }}>{k.label}</Typography>
                                <Typography sx={{ fontSize: 25, fontWeight: 800, lineHeight: 1.2, fontFamily: FONT }}>{k.value}</Typography>
                            </Box>
                        </Paper>
                    ))}
                </Box>

                {/* ── Filter Bar ───────────────────────────────────────── */}
                <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '14px', border: '1px solid #e2e8f0', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField size="small" placeholder="Search name, email, phone or employee ID" value={searchText} onChange={(e) => setSearchText(e.target.value)}
                        sx={{ flex: 1, minWidth: 240 }}
                        InputProps={{ startAdornment: <MdSearch size={20} style={{ marginRight: 8, color: '#94a3b8' }} /> }} />
                    <TextField select size="small" label="Role" value={filterRole} onChange={(e) => setFilterRole(e.target.value)} sx={{ minWidth: 190 }}>
                        <MenuItem value="">All roles</MenuItem>
                        {roleOptions.map((r) => (
                            <MenuItem key={r} value={r}>{r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</MenuItem>
                        ))}
                    </TextField>
                    <TextField select size="small" label="Status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} sx={{ minWidth: 140 }}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                    </TextField>
                    {hasActiveFilters && <Button onClick={clearFilters} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Clear</Button>}
                </Paper>

                {/* ── Table ────────────────────────────────────────────── */}
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(15,23,42,.05)' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ '& th': { backgroundColor: '#f8fafc', fontWeight: 800, py: 1.6, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', fontFamily: FONT, borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' } }}>
                                <TableCell>User</TableCell>
                                <TableCell>Contact</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell align="center">Active</TableCell>
                                <TableCell>Joined</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                Array.from({ length: 6 }).map((_, i) => (
                                    <TableRow key={i}>{Array.from({ length: 6 }).map((__, j) => <TableCell key={j}><Skeleton height={34} /></TableCell>)}</TableRow>
                                ))
                            ) : paginatedUsers.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ py: 6, textAlign: 'center' }}>
                                        <Typography sx={{ fontFamily: FONT, color: '#64748b', fontWeight: 600 }}>{hasActiveFilters ? 'No users match your filters.' : 'No users yet.'}</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : paginatedUsers.map((user) => {
                                const roleColor = getRoleColor(user.role_id?.name || user.role);
                                const active = isActiveUser(user);
                                return (
                                    <TableRow key={user._id || user.id} hover onClick={() => openView(user)} sx={{ cursor: 'pointer', '& td': { borderBottom: '1px solid #f1f5f9', py: 1.4, fontFamily: FONT } }}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Badge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                    badgeContent={<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: active ? '#10b981' : '#ef4444', border: '2px solid #fff' }} />}>
                                                    <Avatar src={user.image} alt={user.firstName} sx={{ width: 38, height: 38, bgcolor: '#ffd102', color: '#000', fontWeight: 800, fontSize: 14 }}>{initialsOf(user)}</Avatar>
                                                </Badge>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5, color: '#0f172a' }}>{user.firstName} {user.lastName}</Typography>
                                                    <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8' }}>{user.empId || `#${(user._id || '').slice(-6).toUpperCase()}`}</Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}><MdEmail size={13} color="#94a3b8" /><Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#334155' }}>{user.email || '—'}</Typography></Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}><MdPhone size={13} color="#94a3b8" /><Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{user.phoneNumber || '—'}</Typography></Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={getRoleLabel(user)} size="small" sx={{ fontFamily: FONT, fontWeight: 700, height: 24, bgcolor: roleColor.bg, color: roleColor.color }} />
                                        </TableCell>
                                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                            <Tooltip title={active ? 'Deactivate (blocks sign-in)' : 'Activate'} arrow>
                                                <span>
                                                    <Switch checked={active} onChange={() => handleStatusToggle(user)} size="small"
                                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#10b981' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#10b981' }, '& .MuiSwitch-track': { bgcolor: '#ef4444' }, '& .MuiSwitch-switchBase': { color: '#ef4444' } }} />
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell><Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#475569' }}>{fmtDate(user.createdAt)}</Typography></TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                                            <Tooltip title="View" arrow><IconButton size="small" onClick={() => openView(user)} sx={{ ...iconBtn('#4f46e5', 'rgba(79,70,229,.1)'), mr: 0.75 }}><MdVisibility size={16} /></IconButton></Tooltip>
                                            {(user.role_id?.name || '').toLowerCase() === 'warehouse_admin' && (
                                                <Tooltip title="Assign all warehouse menus" arrow><IconButton size="small" onClick={() => setGrantTarget(user)} sx={{ ...iconBtn('#059669', '#ecfdf5'), mr: 0.75 }}><MdWarehouse size={16} /></IconButton></Tooltip>
                                            )}
                                            <Tooltip title="Edit" arrow><IconButton size="small" onClick={() => handleEditOpen(user)} sx={{ ...iconBtn('#ca8a04', '#fefce8'), mr: 0.75 }}><MdEdit size={16} /></IconButton></Tooltip>
                                            <Tooltip title="Delete" arrow><IconButton size="small" onClick={() => handleDeleteClick(user)} sx={iconBtn('#dc2626', 'rgba(220,38,38,.1)')}><MdDeleteForever size={18} /></IconButton></Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {filteredUsers.length > PAGE_SIZE && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} shape="rounded" showFirstButton showLastButton
                            sx={{ '& .MuiPaginationItem-root': { fontFamily: FONT, fontWeight: 600, borderRadius: '8px', '&.Mui-selected': { backgroundColor: 'rgb(255, 209, 2)', color: 'black', '&:hover': { backgroundColor: 'rgb(255, 190, 0)' } } } }} />
                    </Box>
                )}
                {filteredUsers.length > 0 && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                        Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, filteredUsers.length)} of {filteredUsers.length} users
                    </Typography>
                )}
            </Box>

            {/* ═══ VIEW DIALOG ══════════════════════════════════════════ */}
            <Dialog open={!!viewUser} onClose={() => setViewUser(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}>
                {viewUser && (() => {
                    const d = viewData?.user || {};
                    const merged = { ...viewUser, ...d };
                    const active = isActiveUser(merged);
                    const rolePicked = getRoleColor(merged.role_id?.name || merged.role);
                    const wh = d.warehouseId && typeof d.warehouseId === 'object' ? d.warehouseId : null;
                    const creator = d.createdBy && typeof d.createdBy === 'object' ? `${d.createdBy.firstName || ''} ${d.createdBy.lastName || ''}`.trim() : '';
                    const menus = viewData?.accessibleMenus;
                    const act = viewData?.activity;
                    const sessions = viewData?.sessions;
                    const roleName = (merged.role_id?.name || '').toLowerCase();
                    const fullAccess = roleName === 'super_admin' || roleName === 'warehouse_admin';
                    const tabs = ['Overview', `Access${menus ? ` (${menus.length})` : ''}`, 'Activity', ...(sessions ? [`Devices (${sessions.length})`] : [])];
                    const Row = ({ k, v }) => (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px solid #f1f5f9', '&:last-child': { borderBottom: 'none' } }}>
                            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{k}</Typography>
                            <Typography component="div" sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' }}>{v === undefined || v === null || v === '' ? '—' : v}</Typography>
                        </Box>
                    );
                    return (
                        <>
                            <Box sx={{ background: 'linear-gradient(135deg,#0f172a,#334155)', color: '#fff', p: 3, position: 'relative' }}>
                                <IconButton onClick={() => setViewUser(null)} size="small" sx={{ position: 'absolute', top: 10, right: 10, color: '#cbd5e1', '&:hover': { color: '#fff' } }}><MdClose size={20} /></IconButton>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
                                    <Avatar src={merged.image} sx={{ width: 84, height: 84, bgcolor: '#ffd102', color: '#000', fontWeight: 800, fontSize: 28, border: '3px solid rgba(255,255,255,.2)' }}>{initialsOf(merged)}</Avatar>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 23, lineHeight: 1.2 }}>{merged.firstName} {merged.lastName}</Typography>
                                        <Typography sx={{ fontFamily: FONT, fontSize: 12.5, opacity: 0.8 }}>{merged.empId || `#${(merged._id || '').slice(-8).toUpperCase()}`} · {merged.email}</Typography>
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
                                            <Chip label={getRoleLabel(merged)} size="small" sx={{ fontFamily: FONT, fontWeight: 700, height: 24, bgcolor: rolePicked.bg, color: rolePicked.color }} />
                                            <Chip label={active ? 'Active' : 'Inactive'} size="small" sx={{ fontFamily: FONT, fontWeight: 700, height: 24, bgcolor: active ? '#dcfce7' : '#fee2e2', color: active ? '#166534' : '#991b1b' }} />
                                            {wh && <Chip icon={<MdWarehouse size={14} />} label={wh.name} size="small" sx={{ fontFamily: FONT, fontWeight: 700, height: 24, bgcolor: '#fce7f3', color: '#9d174d', '& .MuiChip-icon': { color: 'inherit' } }} />}
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                        {[
                                            ['Last sign-in', act?.lastLogin ? relTime(act.lastLogin.createdAt) : viewData ? 'Never' : '…'],
                                            ['Actions logged', viewData ? act?.totalActions ?? 0 : '…'],
                                        ].map(([k, v]) => (
                                            <Box key={k} sx={{ background: 'rgba(255,255,255,.1)', borderRadius: '12px', px: 1.75, py: 1, minWidth: 108 }}>
                                                <Typography sx={{ fontFamily: FONT, fontSize: 11, opacity: 0.75, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{k}</Typography>
                                                <Typography sx={{ fontFamily: FONT, fontSize: 15, fontWeight: 800 }}>{v}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>

                            <Tabs value={Math.min(viewTab, tabs.length - 1)} onChange={(_, v) => setViewTab(v)} variant="scrollable" scrollButtons="auto" sx={{ px: 2, borderBottom: '1px solid #e2e8f0', '& .MuiTab-root': { fontFamily: FONT, fontWeight: 800, textTransform: 'none', fontSize: 14 }, '& .MuiTabs-indicator': { background: 'rgb(255 190 0)', height: 3 } }}>
                                {tabs.map((t) => <Tab key={t} label={t} />)}
                            </Tabs>

                            <DialogContent sx={{ p: 3, minHeight: 300, background: '#f8fafc' }}>
                                {viewTab === 0 && (
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                                        <Paper elevation={0} sx={{ p: 2.25, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                                            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14, mb: 0.5 }}>Contact</Typography>
                                            <Row k="Email" v={merged.email} />
                                            <Row k="Mobile" v={merged.phoneNumber} />
                                            <Row k="Employee ID" v={merged.empId} />
                                        </Paper>
                                        <Paper elevation={0} sx={{ p: 2.25, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                                            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14, mb: 0.5 }}>Account</Typography>
                                            <Row k="Role" v={getRoleLabel(merged)} />
                                            <Row k="Can sign in" v={<Switch size="small" checked={active} onChange={() => handleStatusToggle(merged)} />} />
                                            <Row k="Created by" v={creator || 'System'} />
                                            <Row k="Joined" v={fmtDate(merged.createdAt)} />
                                            <Row k="Last updated" v={fmtDate(merged.updatedAt)} />
                                        </Paper>
                                        {wh && (
                                            <Paper elevation={0} sx={{ p: 2.25, borderRadius: '14px', border: '1px solid #e2e8f0', gridColumn: { md: '1 / -1' } }}>
                                                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14, mb: 0.5 }}>Warehouse they manage</Typography>
                                                <Row k="Name" v={wh.name} />
                                                <Row k="Location" v={[wh.city, wh.state].filter(Boolean).join(', ')} />
                                                <Row k="Address" v={wh.address} />
                                                <Row k="Contact" v={[wh.contactPerson, wh.phoneNumber].filter(Boolean).join(' · ')} />
                                            </Paper>
                                        )}
                                    </Box>
                                )}

                                {viewTab === 1 && (
                                    menus === undefined ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                                    ) : fullAccess ? (
                                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                                            <Typography sx={{ fontFamily: FONT, fontSize: 14 }}>{getRoleLabel(merged)} has access to every {roleName === 'warehouse_admin' ? 'warehouse' : 'admin'} menu with full permissions.</Typography>
                                            {menus.length > 0 && <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b', mt: 0.75 }}>{menus.length} menu(s) are currently assigned.</Typography>}
                                        </Paper>
                                    ) : menus.length === 0 ? (
                                        <Paper elevation={0} sx={{ py: 5, textAlign: 'center', borderRadius: '14px', border: '1px dashed #cbd5e1' }}><Typography sx={{ fontFamily: FONT, color: '#64748b', fontWeight: 600 }}>No menus assigned to this user.</Typography></Paper>
                                    ) : (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            {menus.map((m) => (
                                                <Paper key={m.menuId} elevation={0} sx={{ p: 2, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: (m.submenus || []).length ? 1 : 0 }}>
                                                        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14 }}>{m.menuName}</Typography>
                                                        <PermBadges p={m.permissions} />
                                                    </Box>
                                                    {(m.submenus || []).map((s) => (
                                                        <Box key={s.submenuId} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, py: 0.6, pl: 1.5, borderTop: '1px solid #f1f5f9' }}>
                                                            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#334155' }}>{s.submenuName}</Typography>
                                                            <PermBadges p={s.permissions} />
                                                        </Box>
                                                    ))}
                                                </Paper>
                                            ))}
                                        </Box>
                                    )
                                )}

                                {viewTab === 2 && (
                                    !act ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                                    ) : (
                                        <Paper elevation={0} sx={{ p: 2.25, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                                            <Row k="Last sign-in" v={act.lastLogin ? `${fmtDateTime(act.lastLogin.createdAt)}${act.lastLogin.ipAddress ? ` · ${act.lastLogin.ipAddress}` : ''}` : 'Never'} />
                                            <Row k="Total actions logged" v={act.totalActions} />
                                            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14, mt: 2, mb: 1 }}>Recent activity</Typography>
                                            {act.recent.length === 0 ? (
                                                <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#94a3b8' }}>Nothing recorded yet.</Typography>
                                            ) : act.recent.map((a) => (
                                                <Box key={a._id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.9, borderTop: '1px solid #f1f5f9' }}>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700 }}>{a.action}</Typography>
                                                        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{a.entity}{a.ipAddress ? ` · ${a.ipAddress}` : ''}</Typography>
                                                    </Box>
                                                    <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDateTime(a.createdAt)}</Typography>
                                                </Box>
                                            ))}
                                        </Paper>
                                    )
                                )}

                                {sessions && viewTab === 3 && (
                                    <Paper elevation={0} sx={{ p: 2.25, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14 }}>Signed-in devices</Typography>
                                            <Button size="small" disabled={busy || sessions.length === 0} onClick={signOutEverywhere} sx={{ textTransform: 'none', fontWeight: 800, color: '#dc2626', background: 'rgba(220,38,38,.08)', borderRadius: '8px', px: 1.5 }}>Sign out everywhere</Button>
                                        </Box>
                                        {sessions.length === 0 ? (
                                            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#94a3b8' }}>Not signed in on any device.</Typography>
                                        ) : sessions.map((s) => (
                                            <Box key={s._id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, borderTop: '1px solid #f1f5f9' }}>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700 }}>{describeAgent(s.userAgent)}</Typography>
                                                    <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{s.ip || 'unknown IP'} · signed in {fmtDateTime(s.createdAt)}</Typography>
                                                </Box>
                                                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>active {relTime(s.lastActiveAt)}</Typography>
                                            </Box>
                                        ))}
                                    </Paper>
                                )}
                            </DialogContent>

                            <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid #e2e8f0' }}>
                                <Button onClick={() => { setViewUser(null); handleDeleteClick(merged); }} startIcon={<MdDeleteForever />} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626', borderRadius: '10px' }}>Delete</Button>
                                <Box sx={{ flex: 1 }} />
                                <Button onClick={() => setViewUser(null)} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Close</Button>
                                <Button onClick={() => { const u = merged; setViewUser(null); handleEditOpen(u); }} variant="contained" startIcon={<MdEdit />} sx={{ textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3, '&:hover': { background: 'rgb(255 190 0)' } }}>Edit user</Button>
                            </DialogActions>
                        </>
                    );
                })()}
            </Dialog>

            {/* ═══ DELETE DIALOG ════════════════════════════════════════ */}
            <Dialog open={deleteDialogOpen} onClose={() => !busy && handleCloseDeleteDialog()} PaperProps={{ sx: { borderRadius: '14px', p: 1, minWidth: 380 } }}>
                <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>Delete user?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: FONT }}>
                        <b>{userToDelete?.firstName} {userToDelete?.lastName}</b> will be removed and will no longer be able to sign in. This can't be undone from the panel.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={handleCloseDeleteDialog} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} disabled={busy} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>{busy ? 'Deleting…' : 'Yes, delete'}</Button>
                </DialogActions>
            </Dialog>

            {/* ═══ GRANT WAREHOUSE MENUS ════════════════════════════════ */}
            <Dialog open={!!grantTarget} onClose={() => !busy && setGrantTarget(null)} PaperProps={{ sx: { borderRadius: '14px', p: 1, minWidth: 380 } }}>
                <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>Assign all warehouse menus?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: FONT }}>
                        <b>{grantTarget?.firstName} {grantTarget?.lastName}</b> will get every warehouse menu with full permissions. Menus they already have are kept. They need to sign in again to see the change.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => setGrantTarget(null)} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
                    <Button onClick={confirmGrant} disabled={busy} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, background: '#059669', '&:hover': { background: '#047857' } }}>{busy ? 'Assigning…' : 'Assign menus'}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Usermangement;
